import { useState } from 'react'

import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Box,
  ClickAwayListener,
  Divider,
  Grow,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  Switch,
  Typography
} from '@mui/material'
import { ConsoleTooltip } from '@/components/ConsoleTooltip'

import { ColumnVisibilityItem } from '../hooks/useColumnVisibility'
import { BASE_TABLE_TEST_IDS } from '../TableBase.constants'

type Props = {
  columnVisibility: ColumnVisibilityItem[]
  toggleColumnVisibility: (columnId: string) => void
}

export const ColumnVisibility = ({ columnVisibility, toggleColumnVisibility }: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'column-visibility-popper' : undefined

  return (
    <>
      <ConsoleTooltip title="Column visibility">
        <IconButton
          aria-describedby={id}
          onClick={handleClick}
          data-testid={BASE_TABLE_TEST_IDS.columnVisibilityButton}
        >
          <VisibilityIcon />
        </IconButton>
      </ConsoleTooltip>

      <Popper id={id} open={open} anchorEl={anchorEl} placement="bottom-end" transition>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} timeout={200}>
            <Paper elevation={3} sx={{ minWidth: 150, maxWidth: 300, overflow: 'auto', mt: 1 }}>
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  <Typography variant="subtitle2" sx={{ px: 1.75, pt: 1.5, pb: 1.25 }}>
                    Column Visibility
                  </Typography>

                  <Divider />

                  <List dense>
                    {columnVisibility.map((column) => (
                      <ListItem
                        key={column.id}
                        secondaryAction={
                          <Switch
                            edge="end"
                            size="small"
                            checked={column.visible}
                            onChange={() => toggleColumnVisibility(column.id)}
                          />
                        }
                        sx={{
                          borderRadius: 1,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <ListItemText
                          sx={{ pr: 1 }}
                          primary={column.label}
                          primaryTypographyProps={{
                            variant: 'body2',
                            noWrap: true,
                            title: column.label
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}
